import Routing from './routes';
import { secureStorage } from 'utils/secureStorage';
import 'remixicon/fonts/remixicon.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-calendar/dist/Calendar.css';
import CommonModal from 'components/Modals';
import CommonLoader from 'components/Common/CommonLoader';
import useCommonStore from 'store/common';
import { useEffect, useState, useCallback, useRef } from 'react';
import useAuthStore from 'store/auth';
import { connect, disconnect, setToken } from 'store/socket';
import useZegoCloud from 'hooks/useZegoCloud';
import { isMobile } from 'react-device-detect';
import { getFirebaseToken, messaging, requestPermission } from 'services/firebaseSDK';
import toast, { Toaster } from 'react-hot-toast';
import CustomToast from 'components/Common/CustomToast';
import { onMessage } from 'firebase/messaging';
import UsercentricsConsent from 'components/Common/UsercentricsConsent';
import './index.scss';
import { getRoomIdFromUrl } from 'utils';
import { setNewNotificationAlert } from 'store/user';
import { LANGUAGE_MAPPING, APP_IP_API } from 'constants/index';
import useLocalizationStore from 'store/localization';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { logPageView } from 'services';

function App() {
	const [searchParams] = useSearchParams();
	const requestLanguage = searchParams.get('lang');
	const { isLoading }: any = useCommonStore((state) => state);
	const { setActiveLanguage }: any = useLocalizationStore((state) => state);
	const { token: isAuthenticated }: Record<string, any> = useAuthStore(
		(state) => state.userInfo ?? {},
	);

	// Use the custom hook to manage Zendesk chat script (Commented for the face-2)
	const { createZegoLogin } = useZegoCloud();

	const [hasToken, setHasToken] = useState<boolean>(() => {
		return !!secureStorage.getItem('fcmToken');
	});
	const location = useLocation(); // Get current route
	const navigate = useNavigate();

	// Use refs to prevent multiple API calls
	const fcmSetupRef = useRef(false);
	const localeSetupRef = useRef(false);
	const authSetupRef = useRef(false);
	const randomNavigationRef = useRef(false);

	// Random landing page logic - FIXED: Only run once on mount
	useEffect(() => {
		// Check if we've already done random navigation
		if (randomNavigationRef.current) return;

		// Only perform random navigation if we're on the root path
		if (location.pathname === '/') {
			// Perform random navigation between / and /interactive-demo
			const randomPage = Math.random() < 0.5 ? '/' : '/interactive-demo';
			console.log('Random navigation triggered:', randomPage); // Debug log
			navigate(randomPage);
		}

		// Mark as completed regardless of the path
		randomNavigationRef.current = true;
	}, []); // Empty dependency array - only runs once on mount

	// Memoize page view logging
	const logPageViewCallback = useCallback(() => {
		if (typeof window !== 'undefined') {
			logPageView(location.pathname);
		}
	}, [location.pathname]);

	useEffect(() => {
		logPageViewCallback();
	}, [logPageViewCallback]); // Runs every time the route changes

	// Memoize Firebase token fetching
	const fetchFirebaseToken = useCallback(async () => {
		try {
			const firebaseToken = await getFirebaseToken();
			if (firebaseToken) {
				secureStorage.setItem('fcmToken', firebaseToken);
				setHasToken(true);
			}
		} catch (err) {
			console.error('An error occurred while retrieving Firebase token.', err);
		}
	}, []);

	//Effect for fetching Firebase Token and requesting permissions
	useEffect(() => {
		if (isMobile) return; // Skip for mobile

		// Request permission and fetch token only when needed
		if (!hasToken) {
			requestPermission();
			fetchFirebaseToken();
		} else if (!isAuthenticated) {
			fetchFirebaseToken();
		}
	}, [hasToken, isAuthenticated, fetchFirebaseToken]);

	// Memoize FCM message handling - FIXED: Remove dependencies that cause re-renders
	const handleFCMessage = useCallback(async () => {
		if (fcmSetupRef.current) return; // Prevent multiple setups
		fcmSetupRef.current = true;

		try {
			if (!messaging) {
				console.info('Firebase messaging not available, skipping FCM setup');
				return;
			}

			const unsubscribeFunction = onMessage(messaging, (payload) => {
				try {
					console.log('Message received. ', payload);
					if (payload?.data?.type && payload?.data?.type !== 'chat') {
						// setNewNotificationAlert(true);
					}
					const currentRoomId = getRoomIdFromUrl(window.location.pathname);
					const incomingRoomId = payload?.data?.room_id || 0;

					// Notify user only if room IDs do not match
					if (
						!currentRoomId ||
						!incomingRoomId ||
						Number(currentRoomId) !== Number(incomingRoomId)
					) {
						toast.custom((t: any) => <CustomToast t={t} payload={payload} />, { duration: 5000 });
					}
				} catch (messageError) {
					console.error('Error handling FCM message:', messageError);
				}
			});

			return () => {
				try {
					if (unsubscribeFunction) {
						unsubscribeFunction();
					}
				} catch (unsubscribeError) {
					console.error('Error unsubscribing from FCM:', unsubscribeError);
				}
			};
		} catch (error) {
			console.error('Failed to setup FCM:', error);
			return () => {}; // Return empty cleanup function
		}
	}, []); // Empty dependency array - setup only once

	// Memoize language detection - FIXED: Remove unnecessary dependencies
	const detectLanguage = useCallback(async (countryCode: string) => {
		// Force English if the IP is from India
		if (countryCode?.toUpperCase() === 'IN') {
			return 'en';
		}
		const detectedLanguage = Object.entries(LANGUAGE_MAPPING).find(([_, countries]: any) =>
			countries.includes(countryCode),
		)?.[0];
		return detectedLanguage || 'en';
	}, []); // Empty dependency array - static function

	// Memoize locale fetching - FIXED: Remove dependencies that cause re-renders
	const fetchLocale = useCallback(async () => {
		if (localeSetupRef.current) return; // Prevent multiple calls
		localeSetupRef.current = true;

		try {
			const response = await fetch(APP_IP_API);
			if (!response.ok) throw new Error('Failed to fetch locale');
			const data = await response.json();
			secureStorage.setItem('ipAddress', data.ip);
			window.dispatchEvent(new Event('ipAddressChanged'));

			const detectedLanguage = await detectLanguage(data.country_code);
			setActiveLanguage(detectedLanguage);
		} catch (error) {
			console.error('Error detecting locale:', error);
		}
	}, [detectLanguage, setActiveLanguage]);

	// Effect for handling incoming foreground messages and language setup - FIXED
	useEffect(() => {
		const storedLanguage = localStorage.getItem('i18nextLng'); // not sensitive, keep as is
		if (!storedLanguage || requestLanguage) {
			if (requestLanguage) {
				setActiveLanguage(requestLanguage || 'en');
			} else {
				fetchLocale();
			}
		}

		if (!isMobile && !fcmSetupRef.current) {
			handleFCMessage();
		}
	}, [requestLanguage, setActiveLanguage, fetchLocale, handleFCMessage]);

	// Memoize authentication effect - FIXED: Prevent multiple socket connections
	const handleAuthentication = useCallback(() => {
		if (isAuthenticated && !authSetupRef.current) {
			authSetupRef.current = true;
			setToken(isAuthenticated);
			connect();
			createZegoLogin();

			return () => {
				disconnect();
				authSetupRef.current = false;
			};
		}
	}, [isAuthenticated, createZegoLogin]);

	useEffect(() => {
		handleAuthentication();
	}, [handleAuthentication]);

	return (
		<>
			<Routing />
			<UsercentricsConsent />
			<Toaster position={'top-center'} reverseOrder={false} gutter={1} />
			{isLoading && <CommonLoader />}
			<CommonModal />
		</>
	);
}

export default App;
